import { Truck, Users, Shield, Clock, CheckCircle, Phone, Mail, MapPin, Star, ArrowRight, Target } from 'lucide-react'
import SEOHead from '../components/SEOHead'

export default function Clients() {
  const services = [
    {
      icon: Truck,
      title: "Driver Recruitment",
      description: "Access to 30,000+ pre-vetted drivers across the UK",
      features: [
        "HGV/LGV drivers",
        "PSV drivers", 
        "Van drivers",
        "Multi-drop specialists",
        "Permanent & temporary"
      ]
    },
    {
      icon: Users,
      title: "Engineering & Technical",
      description: "Specialized technical talent for transport operations",
      features: [
        "Transport engineers",
        "Maintenance technicians", 
        "Fleet managers",
        "Operations specialists",
        "Technical supervisors"
      ]
    },
    {
      icon: Shield,
      title: "Compliance & Screening", 
      description: "100% compliant recruitment with full screening",
      features: [
        "DBS checks",
        "Right to work verification",
        "License validation",
        "Reference checks",
        "Medical clearances"
      ]
    },
    {
      icon: Clock,
      title: "Rapid Response",
      description: "Emergency cover and urgent staffing solutions",
      features: [
        "24/7 availability",
        "Same-day placements",
        "Emergency cover",
        "Short-notice staffing",
        "Dedicated account management"
      ]
    }
  ]

  const benefits = [
    "Award-winning service with proven track record",
    "30,000+ pre-screened drivers in our database", 
    "100% compliance guaranteed",
    "24/7 support and emergency cover",
    "Competitive rates with transparent pricing",
    "Dedicated account management",
    "Quick turnaround times",
    "Industry expertise in transport & logistics"
  ]

  const processSteps = [
    {
      step: "1",
      title: "Initial Consultation",
      description: "We discuss your specific requirements, company culture, and staffing needs."
    },
    {
      step: "2", 
      title: "Candidate Sourcing",
      description: "Our team identifies and screens suitable candidates from our extensive database."
    },
    {
      step: "3",
      title: "Compliance Checks",
      description: "Full compliance screening including licenses, references, and background checks."
    },
    {
      step: "4",
      title: "Placement & Support",
      description: "We facilitate the placement and provide ongoing support for successful integration."
    }
  ]

  return (
    <div className="min-h-screen">
      <SEOHead 
        title="Hire Transport & Logistics Staff - C9 Recruitment Client Services"
        description="Access 30,000+ pre-vetted drivers and technical professionals. Emergency cover, permanent placements, and expert recruitment services for transport companies."
        keywords="hire drivers, transport recruitment, logistics staffing, HGV drivers, emergency cover, recruitment services, transport staffing"
      />
      
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-c9-blue via-c9-blue to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-c9-blue/80 to-transparent"></div>
        
        {/* Floating shapes for visual interest */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-c9-orange/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/5 rounded-full blur-lg"></div>
        
        <div className="relative container-full py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 bg-c9-orange/20 rounded-full text-c9-orange font-semibold text-sm mb-6 backdrop-blur-sm">
                Client Services
              </div>
              <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                Hire the Best{" "}
                <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                  Talent
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
                Access 30,000+ pre-vetted drivers and technical professionals. 
                From urgent cover to permanent placements, we deliver quality candidates fast.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="#contact" 
                  className="bg-c9-orange text-white px-8 py-4 rounded-xl font-semibold hover:bg-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 inline-flex items-center justify-center"
                >
                  Get Started Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
                <a 
                  href="tel:02081524574" 
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-c9-blue transition-all duration-300 inline-flex items-center justify-center"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </a>
              </div>
            </div>
            
            {/* Enhanced Stats */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-3xl md:text-4xl font-black text-c9-orange mb-2">30,000+</div>
                <div className="text-white/90 font-semibold">Vetted Drivers</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-3xl md:text-4xl font-black text-c9-orange mb-2">24/7</div>
                <div className="text-white/90 font-semibold">Support</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-3xl md:text-4xl font-black text-c9-orange mb-2">100%</div>
                <div className="text-white/90 font-semibold">Compliant</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-3xl md:text-4xl font-black text-c9-orange mb-2">Award</div>
                <div className="text-white/90 font-semibold">Winning</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Services Section */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-full">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-c9-blue/10 rounded-full text-c9-blue font-semibold text-sm mb-4">
              Our Services
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-c9-blue mb-6">
              Complete{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                Recruitment Solutions
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From drivers to engineers, we provide comprehensive staffing solutions 
              tailored to your transport and logistics needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon
              return (
                <div 
                  key={index} 
                  className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100"
                >
                  <div className="flex items-start space-x-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-c9-orange to-orange-400 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-c9-blue mb-3 group-hover:text-c9-orange transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      <div className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center space-x-3">
                            <CheckCircle className="h-4 w-4 text-c9-orange flex-shrink-0" />
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Enhanced Benefits Section */}
      <section className="section-padding bg-white">
        <div className="container-full">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-c9-orange/10 rounded-full text-c9-orange font-semibold text-sm mb-4">
              Why Choose C9
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-c9-blue mb-6">
              Benefits of Working{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                With Us
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="group bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 hover:from-c9-orange/5 hover:to-orange-50 transition-all duration-300 border border-gray-200 hover:border-c9-orange/20"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-c9-orange to-orange-400 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-gray-700 leading-relaxed font-medium">
                    {benefit}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Quote Section */}
      <section className="section-padding bg-gradient-to-br from-c9-orange via-c9-orange to-orange-400 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* Floating shapes */}
        <div className="absolute top-10 right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
        <div className="absolute bottom-10 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
        
        <div className="relative container-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-white/20 rounded-full text-white font-semibold text-sm mb-6 backdrop-blur-sm">
                Get Your Quote
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Find Your Next{" "}
                <span className="text-white/90">
                  Great Hire?
                </span>
              </h2>
              <p className="text-xl mb-8 text-white/90 leading-relaxed">
                Get a personalized quote for your staffing needs. Our experts will work 
                with you to find the perfect candidates for your business.
              </p>
              <div className="flex items-center space-x-4">
                <Target className="h-8 w-8 text-white/80" />
                <span className="text-lg text-white/90">Tailored solutions for your business</span>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    placeholder="Company Name" 
                    className="w-full p-4 bg-white/90 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-white text-gray-800 placeholder-gray-500"
                  />
                  <input 
                    type="text" 
                    placeholder="Contact Name" 
                    className="w-full p-4 bg-white/90 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-white text-gray-800 placeholder-gray-500"
                  />
                </div>
                <input 
                  type="email" 
                  placeholder="Email Address" 
                  className="w-full p-4 bg-white/90 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-white text-gray-800 placeholder-gray-500"
                />
                <select className="w-full p-4 bg-white/90 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-white text-gray-800">
                  <option>Service Required</option>
                  <option>Driver Recruitment</option>
                  <option>Engineering & Technical</option>
                  <option>Emergency Cover</option>
                  <option>Other</option>
                </select>
                <textarea 
                  placeholder="Tell us about your requirements"
                  rows={4}
                  className="w-full p-4 bg-white/90 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-white text-gray-800 placeholder-gray-500"
                ></textarea>
                <button 
                  type="submit" 
                  className="w-full bg-white text-c9-orange px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Request Quote
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Process Section */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-full">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-c9-blue/10 rounded-full text-c9-blue font-semibold text-sm mb-4">
              Our Process
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-c9-blue mb-6">
              Simple, Efficient,{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                Transparent
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From requirement to placement in 4 easy steps - 
              our streamlined process ensures quick, quality results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((process, index) => (
              <div key={index} className="group text-center">
                <div className="relative mb-8">
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-c9-orange to-orange-400 text-white rounded-3xl flex items-center justify-center text-2xl font-bold group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    {process.step}
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-c9-orange/50 to-gray-300 transform -translate-y-1/2"></div>
                  )}
                </div>
                <h3 className="text-xl font-bold text-c9-blue mb-4 group-hover:text-c9-orange transition-colors duration-300">
                  {process.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {process.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Contact CTA */}
      <section id="contact" className="section-padding bg-gradient-to-br from-c9-blue via-c9-blue to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* Floating shapes */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-c9-orange/10 rounded-full blur-3xl transform translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl transform -translate-x-24 translate-y-24"></div>
        
        <div className="relative container-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Get Started?
                </h2>
                <p className="text-xl text-gray-200 leading-relaxed">
                  Contact our team today for immediate assistance with your staffing needs.
                  We're here to help you find the perfect candidates.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-c9-orange/20 rounded-xl flex items-center justify-center">
                    <Phone className="h-6 w-6 text-c9-orange" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold">Call Us</div>
                    <div className="text-gray-200">0208 152 4574</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-c9-orange/20 rounded-xl flex items-center justify-center">
                    <Mail className="h-6 w-6 text-c9-orange" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold">Email Us</div>
                    <div className="text-gray-200">contact@c9-recruitment.com</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-c9-orange/20 rounded-xl flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-c9-orange" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold">Coverage</div>
                    <div className="text-gray-200">UK Wide Service</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 border border-white/20">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-c9-orange to-orange-400 rounded-3xl flex items-center justify-center">
                  <Star className="h-12 w-12 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Award Winning Service</h3>
                <p className="text-gray-200 mb-8 leading-relaxed">
                  Transport & Logistics Agency of the Year 2023. 
                  Trusted by hundreds of companies across the UK.
                </p>
                <a 
                  href="tel:02081524574" 
                  className="bg-c9-orange text-white px-8 py-4 rounded-xl font-semibold hover:bg-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 inline-flex items-center"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
